/**
 * Single source of truth for localized URL slugs.
 *
 * Every entry has a stable `key`, the EN slug (used as canonical
 * source path in source code), and the SL slug (Slovenian translation).
 *
 * Internal links throughout the codebase keep their EN form (e.g. `/products/welding-heads`)
 * and are translated at render time via `localizePath()` from `./localizedPath`.
 *
 * When adding a new page:
 *   1. Add a route entry below.
 *   2. Add the corresponding `<Route>` in App.tsx (both EN and SL slug auto-generated).
 *   3. Add it to `prerender-routes.ts` / `sitemap.xml` generators (also auto-driven).
 */

export type RouteEntry = {
  key: string;
  en: string; // absolute path WITHOUT lang prefix, e.g. "/products/welding-heads"
  sl: string; // absolute path WITHOUT lang prefix, e.g. "/izdelki/varilne-glave"
};

export const ROUTES: RouteEntry[] = [
  { key: "home",                  en: "/",                                         sl: "/" },

  // Top-level
  { key: "aboutUs",               en: "/about-us",                                 sl: "/o-nas" },
  { key: "knowledgeHub",          en: "/knowledge-hub",                            sl: "/baza-znanja" },
  { key: "newsArticle",           en: "/news/:slug",                               sl: "/novice/:slug" },
  { key: "testimonials",          en: "/testimonials",                             sl: "/mnenja" },
  { key: "usersClients",          en: "/users-clients",                            sl: "/uporabniki-stranke" },
  { key: "partners",              en: "/partners",                                 sl: "/partnerji" },
  { key: "faq",                   en: "/faq",                                      sl: "/pogosta-vprasanja" },
  { key: "services",              en: "/services",                                 sl: "/storitve" },
  { key: "consumables",           en: "/consumables",                              sl: "/potrosni-material" },
  { key: "privacy",               en: "/splosni-pogoji",                           sl: "/splosni-pogoji" },

  // Solutions
  { key: "solutions",             en: "/solutions",                                sl: "/resitve" },
  { key: "solGastronomy",         en: "/solutions/gastronomy-medicine",            sl: "/resitve/gastronomija-medicina" },
  { key: "solSinks",              en: "/solutions/spot-welding-sinks",             sl: "/resitve/tockovno-varjenje-korit" },
  { key: "solRoboticSpotGuns",    en: "/solutions/robotic-spot-welding-guns",      sl: "/resitve/robotske-tockovne-varilne-pistole" },
  { key: "solCompacting",         en: "/solutions/compacting-tools",               sl: "/resitve/orodja-za-stiskanje" },
  { key: "solLock",               en: "/solutions/lock-welding",                   sl: "/resitve/varjenje-kljucavnic" },
  { key: "solTensionRing",        en: "/solutions/tension-ring-welding",           sl: "/resitve/uporovno-varjenje-zateznih-obrocev" },
  { key: "solBarrelHandle",       en: "/solutions/barrel-handle-welding",          sl: "/resitve/varjenje-rocajev-sodov" },
  { key: "solTappingBarrel",      en: "/solutions/tapping-barrel-welding",         sl: "/resitve/uporovno-varjenje-natocnih-sodov" },
  { key: "solProtectiveMesh",     en: "/solutions/protective-mesh-welding",        sl: "/resitve/varjenje-zascitnih-mrez" },
  { key: "solOilTank",            en: "/solutions/oil-tank-welding",               sl: "/resitve/varjenje-oljnih-rezervoarjev" },
  { key: "solDoubleSidedSeamOven",en: "/solutions/double-sided-seam-oven-welding", sl: "/resitve/dvostransko-kolutno-varjenje-pecic" },
  { key: "solChimneyElbow",       en: "/solutions/chimney-elbow-welding",          sl: "/resitve/varjenje-dimniskih-kolen" },
  { key: "solWeldDestrTest",      en: "/solutions/weld-destructive-test",          sl: "/resitve/porusni-test-zvarov" },
  { key: "solAluminumFrame",      en: "/solutions/aluminum-frame-welding",         sl: "/resitve/varjenje-aluminijastih-okvirjev" },
  { key: "solDoorProfileSeam",    en: "/solutions/door-profile-seam-welding",      sl: "/resitve/kolutno-varjenje-vratnega-profila" },
  { key: "solNutWelding",         en: "/solutions/nut-welding",                    sl: "/resitve/varjenje-matic" },

  // Products
  { key: "products",              en: "/products",                                 sl: "/izdelki" },
  { key: "prodPedestal",          en: "/products/industrial-pedestal-welders",     sl: "/izdelki/industrijski-steberni-varilniki" },
  { key: "prodMultiSpot",         en: "/products/multi-spot-welding-machine",      sl: "/izdelki/vec-tockovni-varilni-stroj" },
  { key: "prodProjectionInd",     en: "/products/industrial-projection-welders",   sl: "/izdelki/stroj-za-nevidno-varjenje" },
  { key: "prodSeamInd",           en: "/products/industrial-seam-welders",         sl: "/izdelki/stroji-za-sivno-varjenje" },
  { key: "prodSeamRK94",          en: "/products/seam-welding/rk-94",              sl: "/izdelki/kolutno-varjenje/rk-94" },
  { key: "prodWeldingHeads",      en: "/products/welding-heads",                   sl: "/izdelki/varilne-glave" },
  { key: "prodWeldingPincers",    en: "/products/welding-pincers",                 sl: "/izdelki/varilne-klesce" },
  { key: "prodBattery",           en: "/products/battery-processing",              sl: "/izdelki/obdelava-baterij" },
  { key: "prodWorkstation",       en: "/products/workstation",                     sl: "/izdelki/delovna-postaja" },
  { key: "prodSpotWelding",       en: "/products/spot-welding",                    sl: "/izdelki/tockovno-varjenje" },
  { key: "prodSpotWeldingItem",   en: "/products/spot-welding/:machineId",         sl: "/izdelki/tockovno-varjenje/:machineId" },
  { key: "prodCarSpotters",       en: "/products/car-spotters",                    sl: "/izdelki/avto-spotterji" },
  { key: "prodMicroWelding",      en: "/products/micro-welding",                   sl: "/izdelki/mikrovarjenje" },
  { key: "prodServoHeads",        en: "/products/servo-welding-heads",             sl: "/izdelki/servo-varilne-glave" },
  { key: "prodDoubleHeads",       en: "/products/double-welding-heads",            sl: "/izdelki/dvojne-varilne-glave" },
  { key: "prodButtWelding",       en: "/products/butt-welding",                    sl: "/izdelki/celno-varjenje" },
  { key: "prodProjectionWelding", en: "/products/projection-welding",              sl: "/izdelki/bradavicno-varjenje" },
  { key: "prodICBending",         en: "/products/ic-bending-tool",                 sl: "/izdelki/orodje-za-upogibanje-ic" },
  { key: "prodWeldControls",      en: "/products/welding-controls",                sl: "/izdelki/varilni-krmilniki" },
  { key: "prodWcKtSst",           en: "/products/welding-controls/kt-sst-ac0c4-230", sl: "/izdelki/varilni-krmilniki/kt-sst-ac0c4-230" },
  { key: "prodWcHcs",             en: "/products/welding-controls/high-current-switch", sl: "/izdelki/varilni-krmilniki/visokotokovno-stikalo" },
  { key: "prodWcPxc7100",         en: "/products/welding-controls/pxc7100",        sl: "/izdelki/varilni-krmilniki/pxc7100" },
  { key: "prodWcPrc7300",         en: "/products/welding-controls/prc7300",        sl: "/izdelki/varilni-krmilniki/prc7300" },
  { key: "prodWcISpot",           en: "/products/welding-controls/ispot",          sl: "/izdelki/varilni-krmilniki/ispot" },
  { key: "prodWcPrimus",          en: "/products/welding-controls/primus",         sl: "/izdelki/varilni-krmilniki/primus" },
  { key: "prodWcGenius",          en: "/products/welding-controls/genius",         sl: "/izdelki/varilni-krmilniki/genius" },

  // Spare parts & accessories
  { key: "spareParts",            en: "/spare-parts",                              sl: "/rezervni-deli" },
  { key: "spareBosch",            en: "/spare-parts/bosch-rexroth",                sl: "/rezervni-deli/bosch-rexroth" },
  { key: "spareHarms",            en: "/spare-parts/harms-wende",                  sl: "/rezervni-deli/harms-wende" },
  { key: "spareTransformers",     en: "/spare-parts/transformers",                 sl: "/rezervni-deli/transformatorji" },
  { key: "spareWeldControls",     en: "/spare-parts/welding-controls",             sl: "/rezervni-deli/varilni-krmilniki" },
  { key: "accessories",           en: "/accessories",                              sl: "/dodatna-oprema" },
  { key: "accTipDressers",        en: "/accessories/tip-dressers",                 sl: "/dodatna-oprema/rocni-posnemalec-elektrod" },
];

/**
 * Old Slovenian slugs that have been replaced. Used by App.tsx to emit
 * `<Route … element={<Navigate to=…>}>` so existing inbound links and
 * search-engine results keep working after a slug rename.
 *
 * `from` and `to` are SL paths WITHOUT the `/sl` prefix.
 */
export const LEGACY_SL_REDIRECTS: { from: string; to: string }[] = [
  { from: "/resitve/robotske-tockovne-klesce",            to: "/resitve/robotske-tockovne-varilne-pistole" },
  { from: "/resitve/orodja-za-kompaktiranje",             to: "/resitve/orodja-za-stiskanje" },
  { from: "/resitve/varjenje-napenjalnih-obrocev",        to: "/resitve/uporovno-varjenje-zateznih-obrocev" },
  { from: "/resitve/varjenje-pipnih-sodov",               to: "/resitve/uporovno-varjenje-natocnih-sodov" },
  { from: "/izdelki/industrijski-stojeci-varilni-stroji", to: "/izdelki/industrijski-steberni-varilniki" },
  { from: "/izdelki/industrijski-bradavicni-varilni-stroji", to: "/izdelki/stroj-za-nevidno-varjenje" },
  { from: "/izdelki/industrijski-kolutni-varilni-stroji", to: "/izdelki/stroji-za-sivno-varjenje" },
  { from: "/izdelki/orodje-za-krivljenje-ic",             to: "/izdelki/orodje-za-upogibanje-ic" },
  { from: "/izdelki/tockovno-varjenje/hand-held-welding-gun", to: "/izdelki/tockovno-varjenje/rocni-varilni-tockalnik" },
  { from: "/izdelki/tockovno-varjenje/fast-spotter",         to: "/izdelki/tockovno-varjenje/aparat-za-varjenje-navojnih-cepov" },
];

export const SUPPORTED_LANGS = ["en", "sl"] as const;
export type Lang = (typeof SUPPORTED_LANGS)[number];

/** Lookup tables built once. */
const enToEntry = new Map<string, RouteEntry>();
const slToEntry = new Map<string, RouteEntry>();
for (const r of ROUTES) {
  enToEntry.set(r.en, r);
  slToEntry.set(r.sl, r);
}

/** Returns the route entry whose EN path matches `path` (must include any `:param`s literally). */
export const findByEn = (path: string) => enToEntry.get(path);
/** Returns the route entry whose SL path matches `path`. */
export const findBySl = (path: string) => slToEntry.get(path);

/**
 * Resolve a possibly-parameterized path (e.g. `/products/spot-welding/rs-81`)
 * against the EN templates and return its matching entry plus extracted params.
 */
export function matchEnPath(path: string): { entry: RouteEntry; params: Record<string, string> } | null {
  // Try exact match first
  const exact = enToEntry.get(path);
  if (exact) return { entry: exact, params: {} };

  // Try parameterized routes
  for (const r of ROUTES) {
    if (!r.en.includes(":")) continue;
    const params = matchTemplate(r.en, path);
    if (params) return { entry: r, params };
  }
  return null;
}

export function matchSlPath(path: string): { entry: RouteEntry; params: Record<string, string> } | null {
  const exact = slToEntry.get(path);
  if (exact) return { entry: exact, params: {} };
  for (const r of ROUTES) {
    if (!r.sl.includes(":")) continue;
    const params = matchTemplate(r.sl, path);
    if (params) return { entry: r, params };
  }
  return null;
}

function matchTemplate(template: string, path: string): Record<string, string> | null {
  const tParts = template.split("/");
  const pParts = path.split("/");
  if (tParts.length !== pParts.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < tParts.length; i++) {
    if (tParts[i].startsWith(":")) {
      params[tParts[i].slice(1)] = pParts[i];
    } else if (tParts[i] !== pParts[i]) {
      return null;
    }
  }
  return params;
}

function fillTemplate(template: string, params: Record<string, string>): string {
  return template.replace(/:([a-zA-Z]+)/g, (_, name) => params[name] ?? `:${name}`);
}

/** Public: take an EN-style path and return the path in the target language (still without lang prefix). */
export function translatePath(enPath: string, target: Lang): string {
  const m = matchEnPath(enPath);
  if (!m) return enPath;
  return fillTemplate(m.entry[target], m.params);
}
